/**
 * Segments Dashboard
 * Advanced segment management with visual rule builder
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Target, Plus, Edit, Trash2, Search, Users, Filter,
  X, Save, TrendingUp, DollarSign, Calendar, Tag as TagIcon,
  AlertCircle, CheckCircle, ArrowRight, RefreshCw, Eye, BarChart3
} from 'lucide-react';
import './SegmentsDashboard.css';

function SegmentsDashboard({ apiWithAccessControl }) {
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBuilder, setShowBuilder] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTypeFilter, setActiveTypeFilter] = useState('ALL');
  const [statusMessage, setStatusMessage] = useState(null);
  const [previewModal, setPreviewModal] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [recalculatingId, setRecalculatingId] = useState(null);
  const statusTimerRef = useRef(null);

  useEffect(() => {
    loadSegments();
    return () => {
      if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
    };
  }, []);

  const showStatus = useCallback((type, text) => {
    if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
    setStatusMessage({ type, text });
    statusTimerRef.current = setTimeout(() => {
      setStatusMessage(null);
    }, 3000);
  }, []);

  const loadSegments = async () => {
    setLoading(true);
    try {
      const response = await apiWithAccessControl('/admin/segments');
      const data = await response.json();
      if (data.success) {
        setSegments(data.segments || []);
      }
    } catch (error) {
      console.error('Error loading segments:', error);
      showStatus('error', 'Failed to load segments');
    } finally {
      setLoading(false);
    }
  };

  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const deleteSegment = async (segmentId) => {
    setDeletingId(segmentId);
    try {
      const response = await apiWithAccessControl(`/admin/segments/${segmentId}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        showStatus('success', 'Segment deleted successfully');
        setConfirmDeleteId(null);
        loadSegments();
      }
    } catch (error) {
      console.error('Error deleting segment:', error);
      showStatus('error', 'Failed to delete segment');
    } finally {
      setDeletingId(null);
    }
  };

  const createSegment = () => {
    setSelectedSegment(null);
    setShowBuilder(true);
  };

  const editSegment = (segment) => {
    setSelectedSegment(segment);
    setShowBuilder(true);
  };

  const previewSegment = async (segment) => {
    setPreviewLoading(true);
    setPreviewModal({ segment, recipients: [], recipientCount: 0, loading: true });
    try {
      const response = await apiWithAccessControl(`/admin/segments/${segment.id}/preview?limit=10`);
      const data = await response.json();
      if (data.success) {
        setPreviewModal({
          segment: data.segment || segment,
          recipients: data.previewRecipients || [],
          recipientCount: data.recipientCount || 0,
          loading: false
        });
      } else {
        showStatus('error', 'Failed to load segment preview');
        setPreviewModal(null);
      }
    } catch (error) {
      console.error('Error previewing segment:', error);
      showStatus('error', 'Failed to load segment preview');
      setPreviewModal(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const recalculateSegment = async (segmentId) => {
    setRecalculatingId(segmentId);
    try {
      const response = await apiWithAccessControl(`/admin/segments/${segmentId}/calculate`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        setSegments(prev => prev.map(seg =>
          seg.id === segmentId
            ? { ...seg, recipientCount: data.recipientCount, lastCalculated: data.lastCalculated }
            : seg
        ));
        showStatus('success', `Segment recalculated: ${data.recipientCount} recipients`);
      } else {
        showStatus('error', 'Failed to recalculate segment');
      }
    } catch (error) {
      console.error('Error recalculating segment:', error);
      showStatus('error', 'Failed to recalculate segment');
    } finally {
      setRecalculatingId(null);
    }
  };

  // Stats computations
  const totalRecipients = segments.reduce((sum, seg) => sum + (seg.recipientCount || seg.merchantCount || 0), 0);
  const activeSegments = segments.filter(seg => (seg.recipientCount || seg.merchantCount || 0) > 0).length;
  const typeBreakdown = segments.reduce((acc, seg) => {
    acc[seg.type] = (acc[seg.type] || 0) + 1;
    return acc;
  }, {});

  const TYPE_LABELS = {
    USER: 'Merchants',
    CUSTOMER: 'Customers',
    MIXED: 'Mixed',
    WAITLIST: 'Waitlist'
  };

  const TYPE_COLORS = {
    USER: 'mkt-seg-badge-user',
    CUSTOMER: 'mkt-seg-badge-customer',
    MIXED: 'mkt-seg-badge-mixed',
    WAITLIST: 'mkt-seg-badge-waitlist'
  };

  const FILTER_TABS = [
    { key: 'ALL', label: 'All' },
    { key: 'USER', label: 'Merchants' },
    { key: 'CUSTOMER', label: 'Customers' },
    { key: 'MIXED', label: 'Mixed' },
    { key: 'WAITLIST', label: 'Waitlist' }
  ];

  const filteredSegments = segments.filter(segment => {
    const matchesSearch = searchQuery === '' ||
      segment.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (segment.description && segment.description.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesType = activeTypeFilter === 'ALL' || segment.type === activeTypeFilter;
    return matchesSearch && matchesType;
  });

  // Segment Builder Component
  const SegmentBuilder = () => {
    const [segmentData, setSegmentData] = useState(() => {
      if (selectedSegment) {
        let rules = [];
        let logic = 'AND';

        if (selectedSegment.rules) {
          if (Array.isArray(selectedSegment.rules)) {
            rules = selectedSegment.rules;
          } else if (selectedSegment.rules.rules && Array.isArray(selectedSegment.rules.rules)) {
            rules = selectedSegment.rules.rules;
            logic = selectedSegment.rules.condition || 'AND';
          }
        }

        return {
          ...selectedSegment,
          rules,
          logic: logic || selectedSegment.logic || 'AND'
        };
      }
      return {
        name: '',
        description: '',
        type: 'USER',
        rules: [],
        logic: 'AND'
      };
    });

    const [merchantTierOptions, setMerchantTierOptions] = useState([]);
    const [loadingTiers, setLoadingTiers] = useState(true);
    const [tagOptions, setTagOptions] = useState([]);
    const [loadingTags, setLoadingTags] = useState(true);
    const [tagSearchQuery, setTagSearchQuery] = useState('');
    const [showTagDropdown, setShowTagDropdown] = useState(false);
    const [activeTagRuleIndex, setActiveTagRuleIndex] = useState(null);
    const tagDropdownRef = useRef(null);
    const [builderStatus, setBuilderStatus] = useState(null);
    const [testResult, setTestResult] = useState(null);
    const [testingRules, setTestingRules] = useState(false);
    const builderStatusTimerRef = useRef(null);

    const showBuilderStatus = useCallback((type, text) => {
      if (builderStatusTimerRef.current) clearTimeout(builderStatusTimerRef.current);
      setBuilderStatus({ type, text });
      builderStatusTimerRef.current = setTimeout(() => {
        setBuilderStatus(null);
      }, 3000);
    }, []);

    useEffect(() => {
      return () => {
        if (builderStatusTimerRef.current) clearTimeout(builderStatusTimerRef.current);
      };
    }, []);

    // Fetch merchant tiers dynamically from backend
    useEffect(() => {
      const fetchMerchantTiers = async () => {
        try {
          const response = await apiWithAccessControl('/admin/merchant-tiers');
          const data = await response.json();
          if (data.success && data.tiers) {
            setMerchantTierOptions(data.tiers);
          } else {
            // Fallback: try automation options endpoint which also has dynamic tiers
            try {
              const autoResponse = await apiWithAccessControl('/admin/automation/options');
              const autoData = await autoResponse.json();
              if (autoData.success && autoData.data?.conditions) {
                const tierCondition = autoData.data.conditions.find(c => c.value === 'SUBSCRIPTION_TIER');
                if (tierCondition && tierCondition.options) {
                  setMerchantTierOptions(tierCondition.options);
                  return;
                }
              }
            } catch (e) {
              console.error('Fallback tier fetch also failed:', e);
            }
            setMerchantTierOptions([]);
          }
        } catch (error) {
          console.error('Error fetching merchant tiers:', error);
          setMerchantTierOptions([]);
        } finally {
          setLoadingTiers(false);
        }
      };

      fetchMerchantTiers();
    }, []);

    // Fetch tags dynamically from backend
    useEffect(() => {
      const fetchTags = async () => {
        try {
          const response = await apiWithAccessControl('/admin/tags?type=user');
          const data = await response.json();
          if (data && data.tags) {
            setTagOptions(data.tags.map(t => ({
              value: t.tag || t.name,
              label: t.tag || t.name,
              count: t.count || 0
            })));
          }
        } catch (error) {
          console.error('Error fetching tags:', error);
        } finally {
          setLoadingTags(false);
        }
      };
      fetchTags();
    }, []);

    // Close tag dropdown on outside click
    useEffect(() => {
      const handleClickOutside = (e) => {
        if (tagDropdownRef.current && !tagDropdownRef.current.contains(e.target)) {
          setShowTagDropdown(false);
          setActiveTagRuleIndex(null);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filteredTagOptions = tagOptions.filter(t =>
      t.label.toLowerCase().includes(tagSearchQuery.toLowerCase())
    );

    const RULE_FIELDS = [
      { value: 'merchantTier', label: 'Merchant Tier', type: 'select', options: merchantTierOptions },
      { value: 'totalOrders', label: 'Total Orders', type: 'number' },
      { value: 'totalRevenue', label: 'Total Revenue', type: 'number' },
      { value: 'daysSinceSignup', label: 'Days Since Signup', type: 'number' },
      { value: 'daysSinceLastOrder', label: 'Days Since Last Order', type: 'number' },
      { value: 'hasTag', label: 'Tag', type: 'tag' },
      { value: 'productCount', label: 'Product Count', type: 'number' },
      { value: 'averageOrderValue', label: 'Average Order Value', type: 'number' },
      { value: 'isActive', label: 'Store Active', type: 'select', options: [
        { value: 'true', label: 'Yes' },
        { value: 'false', label: 'No' }
      ]},
      { value: 'emailVerified', label: 'Email Verified', type: 'select', options: [
        { value: 'true', label: 'Yes' },
        { value: 'false', label: 'No' }
      ]},
      { value: 'kycStatus', label: 'KYC Status', type: 'select', options: [
        { value: 'APPROVED', label: 'Approved' },
        { value: 'PENDING_REVIEW', label: 'Pending Review' },
        { value: 'REJECTED', label: 'Rejected' },
        { value: 'NOT_STARTED', label: 'Not Started' }
      ]},
      { value: 'kycLevel', label: 'KYC Level', type: 'select', options: [
        { value: 'LEVEL_0', label: 'Level 0' },
        { value: 'LEVEL_1', label: 'Level 1' },
        { value: 'LEVEL_2', label: 'Level 2' },
        { value: 'LEVEL_3', label: 'Level 3' }
      ]},
      { value: 'signupDateAfter', label: 'Joined After', type: 'date' },
      { value: 'signupDateBefore', label: 'Joined Before', type: 'date' },
      { value: 'isWaitlistMember', label: 'Waitlist Member', type: 'select', options: [
        { value: 'true', label: 'Yes (From Waitlist)' },
        { value: 'false', label: 'No (Direct Signup)' }
      ]}
    ];

    const getOperatorsForField = (fieldType) => {
      if (fieldType === 'select') {
        return [
          { value: 'equals', label: 'Equals' },
          { value: 'not_equals', label: 'Not Equals' }
        ];
      }
      if (fieldType === 'tag') {
        return [
          { value: 'contains', label: 'Has Tag' },
          { value: 'not_contains', label: 'Does Not Have Tag' }
        ];
      }
      if (fieldType === 'number') {
        return [
          { value: 'equals', label: 'Equals' },
          { value: 'not_equals', label: 'Not Equals' },
          { value: 'greater_than', label: 'Greater Than' },
          { value: 'less_than', label: 'Less Than' },
          { value: 'greater_or_equal', label: 'Greater or Equal' },
          { value: 'less_or_equal', label: 'Less or Equal' }
        ];
      }
      if (fieldType === 'date') {
        return [
          { value: 'equals', label: 'On Date' },
          { value: 'greater_than', label: 'After' },
          { value: 'less_than', label: 'Before' },
          { value: 'greater_or_equal', label: 'On or After' },
          { value: 'less_or_equal', label: 'On or Before' }
        ];
      }
      return [
        { value: 'equals', label: 'Equals' },
        { value: 'not_equals', label: 'Not Equals' },
        { value: 'contains', label: 'Contains' },
        { value: 'not_contains', label: 'Does Not Contain' }
      ];
    };

    const addRule = () => {
      setSegmentData({
        ...segmentData,
        rules: [...segmentData.rules, { field: '', operator: 'equals', value: '' }]
      });
    };

    const updateRule = (index, key, value) => {
      const updatedRules = [...segmentData.rules];
      updatedRules[index] = { ...updatedRules[index], [key]: value };
      setSegmentData({ ...segmentData, rules: updatedRules });
    };

    const removeRule = (index) => {
      const updatedRules = segmentData.rules.filter((_, i) => i !== index);
      setSegmentData({ ...segmentData, rules: updatedRules });
    };

    const testRules = async () => {
      if (segmentData.rules.length === 0) {
        showBuilderStatus('error', 'Please add at least one rule to test');
        return;
      }

      const incompleteRule = segmentData.rules.find(r => !r.field || !r.value);
      if (incompleteRule) {
        showBuilderStatus('error', 'Please complete all rules before testing');
        return;
      }

      setTestingRules(true);
      setTestResult(null);
      try {
        const response = await apiWithAccessControl('/admin/segments/test', {
          method: 'POST',
          body: JSON.stringify({
            type: segmentData.type,
            rules: {
              condition: segmentData.logic,
              rules: segmentData.rules
            }
          })
        });
        const data = await response.json();
        if (data.success) {
          setTestResult({
            recipientCount: data.recipientCount,
            preview: (data.preview || []).slice(0, 5)
          });
        } else {
          showBuilderStatus('error', 'Failed to test rules');
        }
      } catch (error) {
        console.error('Error testing rules:', error);
        showBuilderStatus('error', 'Failed to test rules');
      } finally {
        setTestingRules(false);
      }
    };

    const saveSegment = async () => {
      if (!segmentData.name.trim()) {
        showBuilderStatus('error', 'Please enter a segment name');
        return;
      }

      if (segmentData.rules.length === 0) {
        showBuilderStatus('error', 'Please add at least one rule');
        return;
      }

      try {
        const url = selectedSegment
          ? `/admin/segments/${selectedSegment.id}`
          : '/admin/segments';
        const method = selectedSegment ? 'PUT' : 'POST';

        const response = await apiWithAccessControl(url, {
          method,
          body: JSON.stringify(segmentData)
        });

        const data = await response.json();
        if (data.success) {
          showStatus('success', data.message || 'Segment saved successfully');
          setShowBuilder(false);
          loadSegments();
        } else {
          showBuilderStatus('error', data.message || 'Failed to save segment');
        }
      } catch (error) {
        console.error('Error saving segment:', error);
        showBuilderStatus('error', 'Failed to save segment');
      }
    };

    return (
      <div className="mkt-seg-builder">
        <div className="mkt-seg-builder-header">
          <h2>{selectedSegment ? 'Edit Segment' : 'Create New Segment'}</h2>
          <button className="mkt-seg-btn-icon" onClick={() => setShowBuilder(false)}>
            <X size={20} />
          </button>
        </div>

        {/* Builder Status Message */}
        {builderStatus && (
          <div className={`mkt-seg-status-banner mkt-seg-status-${builderStatus.type}`}>
            {builderStatus.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
            <span>{builderStatus.text}</span>
          </div>
        )}

        <div className="mkt-seg-builder-body">
          {/* Basic Info */}
          <div className="mkt-seg-builder-section">
            <h3>Segment Information</h3>
            <div className="mkt-seg-form-group">
              <label>Segment Name *</label>
              <input
                type="text"
                value={segmentData.name}
                onChange={(e) => setSegmentData({ ...segmentData, name: e.target.value })}
                placeholder="e.g., High Value Customers"
              />
            </div>
            <div className="mkt-seg-form-group">
              <label>Description</label>
              <textarea
                value={segmentData.description || ''}
                onChange={(e) => setSegmentData({ ...segmentData, description: e.target.value })}
                placeholder="Describe this segment..."
                rows={3}
              />
            </div>
            <div className="mkt-seg-form-group">
              <label>Segment Type</label>
              <select
                value={segmentData.type}
                onChange={(e) => setSegmentData({ ...segmentData, type: e.target.value })}
              >
                <option value="USER">Merchants (Users)</option>
                <option value="CUSTOMER">Customers</option>
                <option value="MIXED">Mixed (Merchants & Customers)</option>
                <option value="WAITLIST">Waitlist Subscribers</option>
              </select>
              <p className="mkt-seg-field-hint">
                {segmentData.type === 'USER' && 'Target registered merchants on your platform'}
                {segmentData.type === 'CUSTOMER' && 'Target customers who have made purchases'}
                {segmentData.type === 'MIXED' && 'Target both merchants and their customers'}
                {segmentData.type === 'WAITLIST' && 'Target people who signed up for the waitlist'}
              </p>
            </div>
          </div>

          {/* Rules */}
          <div className="mkt-seg-builder-section">
            <h3><Filter size={20} /> Segment Rules</h3>
            <p className="mkt-seg-section-desc">Define criteria to match merchants</p>

            <div className="mkt-seg-form-group">
              <label>Match Logic</label>
              <select
                value={segmentData.logic}
                onChange={(e) => setSegmentData({ ...segmentData, logic: e.target.value })}
              >
                <option value="AND">Match ALL rules (AND)</option>
                <option value="OR">Match ANY rule (OR)</option>
              </select>
            </div>

            {/* Rule List */}
            {segmentData.rules.length > 0 && (
              <div className="mkt-seg-rules-list">
                {segmentData.rules.map((rule, index) => {
                  const selectedField = RULE_FIELDS.find(f => f.value === rule.field);
                  return (
                    <div key={index} className="mkt-seg-rule-row">
                      {/* Field */}
                      <select
                        value={rule.field}
                        onChange={(e) => updateRule(index, 'field', e.target.value)}
                        className="mkt-seg-rule-select"
                      >
                        <option value="">Select field...</option>
                        {RULE_FIELDS.map((field) => (
                          <option key={field.value} value={field.value}>
                            {field.label}
                          </option>
                        ))}
                      </select>

                      {/* Operator */}
                      <select
                        value={rule.operator}
                        onChange={(e) => updateRule(index, 'operator', e.target.value)}
                        className="mkt-seg-rule-select"
                      >
                        {getOperatorsForField(selectedField?.type || 'text').map((op) => (
                          <option key={op.value} value={op.value}>
                            {op.label}
                          </option>
                        ))}
                      </select>

                      {/* Value */}
                      {selectedField && selectedField.type === 'select' ? (
                        <select
                          value={rule.value}
                          onChange={(e) => updateRule(index, 'value', e.target.value)}
                          className="mkt-seg-rule-select"
                        >
                          <option value="">Select value...</option>
                          {selectedField.options.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      ) : selectedField && selectedField.type === 'tag' ? (
                        <div className="mkt-seg-searchable-select" ref={activeTagRuleIndex === index ? tagDropdownRef : null}>
                          <input
                            type="text"
                            className="mkt-seg-searchable-input"
                            placeholder={loadingTags ? 'Loading tags...' : 'Search or select a tag...'}
                            value={activeTagRuleIndex === index ? tagSearchQuery : (rule.value || '')}
                            onChange={(e) => {
                              setTagSearchQuery(e.target.value);
                              setActiveTagRuleIndex(index);
                              setShowTagDropdown(true);
                            }}
                            onFocus={() => {
                              setActiveTagRuleIndex(index);
                              setTagSearchQuery(rule.value || '');
                              setShowTagDropdown(true);
                            }}
                          />
                          {rule.value && (
                            <button
                              type="button"
                              className="mkt-seg-searchable-clear"
                              onClick={() => {
                                updateRule(index, 'value', '');
                                setTagSearchQuery('');
                              }}
                            >
                              <X size={12} />
                            </button>
                          )}
                          {showTagDropdown && activeTagRuleIndex === index && (
                            <div className="mkt-seg-searchable-dropdown">
                              {loadingTags ? (
                                <div className="mkt-seg-searchable-loading">Loading tags...</div>
                              ) : filteredTagOptions.length === 0 ? (
                                <div className="mkt-seg-searchable-empty">
                                  {tagSearchQuery ? 'No matching tags' : 'No tags available'}
                                </div>
                              ) : (
                                filteredTagOptions.map(tag => (
                                  <div
                                    key={tag.value}
                                    className={`mkt-seg-searchable-option ${rule.value === tag.value ? 'selected' : ''}`}
                                    onClick={() => {
                                      updateRule(index, 'value', tag.value);
                                      setShowTagDropdown(false);
                                      setActiveTagRuleIndex(null);
                                      setTagSearchQuery('');
                                    }}
                                  >
                                    <span>{tag.label}</span>
                                    <span className="mkt-seg-searchable-tag-count">{tag.count}</span>
                                  </div>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <input
                          type={
                            selectedField && selectedField.type === 'number' ? 'number' :
                            selectedField && selectedField.type === 'date' ? 'date' : 'text'
                          }
                          value={rule.value}
                          onChange={(e) => updateRule(index, 'value', e.target.value)}
                          placeholder={selectedField && selectedField.type === 'date' ? '' : 'Enter value...'}
                          className="mkt-seg-rule-input"
                        />
                      )}

                      {/* Remove Button */}
                      <button
                        className="mkt-seg-btn-icon danger"
                        onClick={() => removeRule(index)}
                        title="Remove rule"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Add Rule & Test Rules Buttons */}
            <div className="mkt-seg-builder-rule-actions">
              <button
                className="mkt-seg-btn-secondary"
                onClick={addRule}
              >
                <Plus size={16} />
                Add Rule
              </button>
              <button
                className="mkt-seg-btn-test"
                onClick={testRules}
                disabled={testingRules || segmentData.rules.length === 0}
              >
                <Target size={16} />
                {testingRules ? 'Testing...' : 'Test Rules'}
              </button>
            </div>

            {/* Test Result */}
            {testResult && (
              <div className="mkt-seg-test-result">
                <div className="mkt-seg-test-result-header">
                  <CheckCircle size={16} />
                  <strong>{testResult.recipientCount}</strong> matching recipients found
                </div>
                {testResult.preview.length > 0 && (
                  <div className="mkt-seg-test-result-list">
                    <table className="mkt-seg-preview-table">
                      <thead>
                        <tr>
                          <th>Email</th>
                          <th>Name</th>
                          <th>Tier</th>
                        </tr>
                      </thead>
                      <tbody>
                        {testResult.preview.map((recipient, idx) => (
                          <tr key={idx}>
                            <td>{recipient.email || '--'}</td>
                            <td>{recipient.username || recipient.name || '--'}</td>
                            <td>{recipient.tier || recipient.merchantTier || '--'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="mkt-seg-builder-footer">
          <button className="mkt-seg-btn-secondary" onClick={() => setShowBuilder(false)}>
            Cancel
          </button>
          <button className="mkt-seg-btn-primary" onClick={saveSegment}>
            {selectedSegment ? 'Update Segment' : 'Create Segment'}
          </button>
        </div>
      </div>
    );
  };

  // Preview Modal Component
  const PreviewModal = () => {
    if (!previewModal) return null;

    return (
      <div className="mkt-seg-modal-overlay" onClick={() => setPreviewModal(null)}>
        <div className="mkt-seg-modal" onClick={(e) => e.stopPropagation()}>
          <div className="mkt-seg-builder-header">
            <div>
              <h2>Segment Preview</h2>
              <p className="mkt-seg-preview-subtitle">
                {previewModal.segment.name}
                <span className={`mkt-seg-type-badge ${TYPE_COLORS[previewModal.segment.type] || ''}`}>
                  {TYPE_LABELS[previewModal.segment.type] || previewModal.segment.type}
                </span>
              </p>
            </div>
            <button className="mkt-seg-btn-icon" onClick={() => setPreviewModal(null)}>
              <X size={20} />
            </button>
          </div>
          <div className="mkt-seg-preview-body">
            {previewModal.loading ? (
              <div className="mkt-seg-loading">Loading preview...</div>
            ) : (
              <>
                <div className="mkt-seg-preview-count">
                  <Users size={18} />
                  <strong>{previewModal.recipientCount}</strong> total recipients
                </div>
                {previewModal.recipients.length > 0 ? (
                  <div className="mkt-seg-preview-table-wrap">
                    <table className="mkt-seg-preview-table">
                      <thead>
                        <tr>
                          <th>Email</th>
                          <th>Name</th>
                          <th>Tier</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewModal.recipients.map((recipient, idx) => (
                          <tr key={idx}>
                            <td>{recipient.email || '--'}</td>
                            <td>{recipient.username || recipient.name || '--'}</td>
                            <td>{recipient.tier || recipient.merchantTier || '--'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {previewModal.recipientCount > previewModal.recipients.length && (
                      <p className="mkt-seg-preview-more">
                        Showing {previewModal.recipients.length} of {previewModal.recipientCount} recipients
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="mkt-seg-preview-empty">
                    <Users size={32} />
                    <p>No matching recipients found</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mkt-seg-dashboard">
      {/* Status Banner */}
      {statusMessage && (
        <div className={`mkt-seg-status-banner mkt-seg-status-${statusMessage.type}`}>
          {statusMessage.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          <span>{statusMessage.text}</span>
          <button className="mkt-seg-status-close" onClick={() => setStatusMessage(null)}>
            <X size={14} />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="mkt-seg-header">
        <div>
          <h1>Audience Segments</h1>
          <p>Create dynamic merchant segments based on rules and criteria</p>
        </div>
        <button className="mkt-seg-btn-primary" onClick={createSegment}>
          <Plus size={18} />
          Create Segment
        </button>
      </div>

      {/* Stats Overview */}
      {!loading && segments.length > 0 && (
        <div className="mkt-seg-stats-grid">
          <div className="mkt-seg-stat-card">
            <div className="mkt-seg-stat-icon">
              <Target size={20} />
            </div>
            <div className="mkt-seg-stat-content">
              <span className="mkt-seg-stat-value">{segments.length}</span>
              <span className="mkt-seg-stat-label">Total Segments</span>
            </div>
          </div>
          <div className="mkt-seg-stat-card">
            <div className="mkt-seg-stat-icon mkt-seg-stat-icon-blue">
              <Users size={20} />
            </div>
            <div className="mkt-seg-stat-content">
              <span className="mkt-seg-stat-value">{totalRecipients.toLocaleString()}</span>
              <span className="mkt-seg-stat-label">Total Recipients</span>
            </div>
          </div>
          <div className="mkt-seg-stat-card">
            <div className="mkt-seg-stat-icon mkt-seg-stat-icon-green">
              <CheckCircle size={20} />
            </div>
            <div className="mkt-seg-stat-content">
              <span className="mkt-seg-stat-value">{activeSegments}</span>
              <span className="mkt-seg-stat-label">Active Segments</span>
            </div>
          </div>
          <div className="mkt-seg-stat-card">
            <div className="mkt-seg-stat-icon mkt-seg-stat-icon-purple">
              <BarChart3 size={20} />
            </div>
            <div className="mkt-seg-stat-content">
              <span className="mkt-seg-stat-value">{Object.keys(typeBreakdown).length}</span>
              <span className="mkt-seg-stat-label">
                Types: {Object.entries(typeBreakdown).map(([type, count]) =>
                  `${TYPE_LABELS[type] || type} (${count})`
                ).join(', ')}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Search + Filter Tabs */}
      <div className="mkt-seg-search-bar">
        <div className="mkt-seg-search">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search segments..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Type Filter Tabs */}
      {!loading && segments.length > 0 && (
        <div className="mkt-seg-filter-tabs">
          {FILTER_TABS.map(tab => (
            <button
              key={tab.key}
              className={`mkt-seg-filter-tab ${activeTypeFilter === tab.key ? 'mkt-seg-filter-tab-active' : ''}`}
              onClick={() => setActiveTypeFilter(tab.key)}
            >
              {tab.label}
              {tab.key === 'ALL'
                ? ` (${segments.length})`
                : typeBreakdown[tab.key] ? ` (${typeBreakdown[tab.key]})` : ''
              }
            </button>
          ))}
        </div>
      )}

      {/* Segments List */}
      {loading ? (
        <div className="mkt-seg-loading">Loading segments...</div>
      ) : filteredSegments.length === 0 ? (
        <div className="mkt-seg-empty">
          <Target size={64} />
          <h3>{segments.length === 0 ? 'No segments yet' : 'No matching segments'}</h3>
          <p>
            {segments.length === 0
              ? 'Create your first segment to organize and target merchants'
              : 'Try adjusting your search or filter criteria'
            }
          </p>
          {segments.length === 0 && (
            <button className="mkt-seg-btn-primary" onClick={createSegment}>
              <Plus size={18} />
              Create First Segment
            </button>
          )}
        </div>
      ) : (
        <div className="mkt-seg-grid">
          {filteredSegments.map((segment) => (
            <div key={segment.id} className="mkt-seg-card">
              <div className="mkt-seg-card-header">
                <div className="mkt-seg-title">
                  <div className="mkt-seg-title-row">
                    <h3>{segment.name}</h3>
                    <span className={`mkt-seg-type-badge ${TYPE_COLORS[segment.type] || ''}`}>
                      {TYPE_LABELS[segment.type] || segment.type}
                    </span>
                  </div>
                  <span className="mkt-seg-count">
                    <Users size={14} />
                    {segment.recipientCount || segment.merchantCount || 0} recipients
                  </span>
                </div>
                <div className="mkt-seg-actions">
                  <button
                    className="mkt-seg-btn-icon"
                    onClick={() => previewSegment(segment)}
                    title="Preview Recipients"
                  >
                    <Eye size={16} />
                  </button>
                  <button
                    className={`mkt-seg-btn-icon ${recalculatingId === segment.id ? 'mkt-seg-spinning' : ''}`}
                    onClick={() => recalculateSegment(segment.id)}
                    title="Recalculate"
                    disabled={recalculatingId === segment.id}
                  >
                    <RefreshCw size={16} />
                  </button>
                  <button
                    className="mkt-seg-btn-icon"
                    onClick={() => editSegment(segment)}
                    title="Edit"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    className="mkt-seg-btn-icon danger"
                    onClick={() => setConfirmDeleteId(segment.id)}
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {segment.description && (
                <p className="mkt-seg-desc">{segment.description}</p>
              )}

              {/* Visual Rule Display */}
              <div className="mkt-seg-rules">
                <div className="mkt-seg-logic-badge">
                  {(segment.rules?.condition || segment.logic) === 'AND' ? 'Match ALL' : 'Match ANY'}
                </div>
                <div className="mkt-seg-rules-count">
                  <Filter size={14} />
                  {Array.isArray(segment.rules)
                    ? segment.rules.length
                    : (segment.rules?.rules?.length || 0)} rules
                </div>
              </div>

              {/* Preview first 3 rules */}
              {((Array.isArray(segment.rules) && segment.rules.length > 0) ||
                (segment.rules?.rules && segment.rules.rules.length > 0)) && (
                <div className="mkt-seg-rules-preview">
                  {(Array.isArray(segment.rules)
                    ? segment.rules
                    : segment.rules.rules).slice(0, 3).map((rule, index) => (
                    <div key={index} className="mkt-seg-rule-item">
                      <span className="mkt-seg-rule-field">{rule.field}</span>
                      <ArrowRight size={12} />
                      <span className="mkt-seg-rule-operator">{rule.operator}</span>
                      <ArrowRight size={12} />
                      <span className="mkt-seg-rule-value">{rule.value}</span>
                    </div>
                  ))}
                  {((Array.isArray(segment.rules) ? segment.rules.length : segment.rules?.rules?.length) || 0) > 3 && (
                    <div className="mkt-seg-rule-more">
                      +{((Array.isArray(segment.rules) ? segment.rules.length : segment.rules.rules.length) - 3)} more rules
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Segment Builder Modal */}
      {showBuilder && (
        <div className="mkt-seg-modal-overlay">
          <div className="mkt-seg-modal large">
            <SegmentBuilder />
          </div>
        </div>
      )}

      {/* Preview Modal */}
      <PreviewModal />

      {/* Delete Confirmation Dialog */}
      {confirmDeleteId && (
        <div className="mkt-seg-confirm-overlay" onClick={() => setConfirmDeleteId(null)}>
          <div className="mkt-seg-confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="mkt-seg-confirm-icon">
              <Trash2 size={24} />
            </div>
            <h3>Delete Segment</h3>
            <p>Are you sure you want to delete this segment? This action cannot be undone.</p>
            <div className="mkt-seg-confirm-actions">
              <button
                className="mkt-seg-btn-secondary"
                onClick={() => setConfirmDeleteId(null)}
                disabled={deletingId}
              >
                Cancel
              </button>
              <button
                className="mkt-seg-btn-danger"
                onClick={() => deleteSegment(confirmDeleteId)}
                disabled={deletingId}
              >
                {deletingId ? 'Deleting...' : 'Delete Segment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SegmentsDashboard;
